import { useEffect, useRef, useState } from "react";
import {
  ArrowDownToLine,
  ArrowUpRight,
  Check,
  ChevronRight,
  Ellipsis,
  Info,
  LoaderCircle,
  Plus,
  RefreshCw,
  Share,
  ShieldCheck,
  WifiOff,
  X,
} from "lucide-react";
import { useRegisterSW } from "virtual:pwa-register/react";
import { config } from "../config";
import { loadDestination, type DestinationConfig } from "./destination";
import { initializeAnalytics, track } from "./analytics";
import { useViewer } from "../members/Viewer";
import "../members/account.css";

type InstallPrompt = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: string }>;
};
const isStandalone = () =>
  matchMedia("(display-mode: standalone)").matches ||
  Boolean((navigator as Navigator & { standalone?: boolean }).standalone);

export default function PortalApp() {
  const viewer = useViewer();
  const [saving, setSaving] = useState(false);
  const [destination, setDestination] = useState<DestinationConfig | null>(
    null,
  );
  const [error, setError] = useState("");
  const [attempt, setAttempt] = useState(0);
  const [frameLoaded, setFrameLoaded] = useState(false);
  const [frameSlow, setFrameSlow] = useState(false);
  const [dialog, setDialog] = useState<"install" | "about" | null>(null);
  const [standalone, setStandalone] = useState(isStandalone);
  const [installPrompt, setInstallPrompt] = useState<InstallPrompt | null>(
    null,
  );
  const [installing, setInstalling] = useState(false);
  const dialogRef = useRef<HTMLDialogElement>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const openedTarget = useRef("");
  const {
    needRefresh: [needRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(_url, registration) {
      if (registration) void registration.update().catch(() => {});
    },
  });

  useEffect(() => {
    void initializeAnalytics();
    const onDemoEvent = (event: MessageEvent) => {
      if (event.origin !== location.origin || event.source !== iframeRef.current?.contentWindow || event.data?.type !== "yestv:demo") return;
      if (["demo_play", "demo_pause", "demo_ended"].includes(event.data.event)) track(event.data.event);
    };
    addEventListener("message", onDemoEvent);
    return () => removeEventListener("message", onDemoEvent);
  }, []);

  useEffect(() => {
    if (destination?.enabled && destination.destination.mode === "embed" && openedTarget.current !== destination.revision) {
      openedTarget.current = destination.revision;
      track("video_open");
    }
  }, [destination]);

  useEffect(() => {
    document.title =
      config.environment === "production"
        ? "Yestv · Watch"
        : "Yestv · Playback test";
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 8000);
    let active = true;
    setError("");
    setDestination(null);
    setFrameLoaded(false);
    setFrameSlow(false);
    loadDestination(controller.signal)
      .then((value) => {
        if (active) setDestination(value);
      })
      .catch(() => {
        if (active)
          setError(
            navigator.onLine
              ? "We couldn’t connect to the video website."
              : "Connect to the internet to start watching.",
          );
      })
      .finally(() => clearTimeout(timeout));
    return () => {
      active = false;
      clearTimeout(timeout);
      controller.abort();
    };
  }, [attempt]);

  useEffect(() => {
    if (
      !destination ||
      !destination.enabled ||
      destination.destination.mode !== "embed" ||
      frameLoaded
    )
      return;
    const timeout = setTimeout(() => setFrameSlow(true), 12000);
    return () => clearTimeout(timeout);
  }, [destination, frameLoaded]);

  useEffect(() => {
    const beforeInstall = (event: Event) => {
      event.preventDefault();
      setInstallPrompt(event as InstallPrompt);
    };
    const installed = () => {
      track("install_reported");
      setInstallPrompt(null);
      setStandalone(isStandalone());
      setDialog(null);
    };
    const updateMode = () => setStandalone(isStandalone());
    const media = matchMedia("(display-mode: standalone)");
    addEventListener("beforeinstallprompt", beforeInstall);
    addEventListener("appinstalled", installed);
    media.addEventListener("change", updateMode);
    return () => {
      removeEventListener("beforeinstallprompt", beforeInstall);
      removeEventListener("appinstalled", installed);
      media.removeEventListener("change", updateMode);
    };
  }, []);

  useEffect(() => {
    if (!dialog) return;
    const element = dialogRef.current;
    element?.showModal();
    return () => element?.close();
  }, [dialog]);

  async function install() {
    track("install_prompt");
    if (!installPrompt) {
      setDialog("install");
      return;
    }
    setInstalling(true);
    try {
      await installPrompt.prompt();
      await installPrompt.userChoice;
      setInstallPrompt(null);
    } finally {
      setInstalling(false);
    }
  }

  const target = destination?.destination;
  const blocked =
    error ||
    (destination && !destination.enabled
      ? "Yestv is taking a short break. Please try again later."
      : "");

  return (
    <div className="portal-page">
      <main className="portal-app">
        <header className="portal-header">
          <a className="wordmark" href="/" aria-label="Yestv home">
            yes<span>tv</span>
            <i>.</i>
          </a>
          <div className="portal-header-actions">
            <a className="viewer-account-link" href="/account">{viewer.user?.nickname || "My account"}</a>
            {config.environment !== "production" && (
              <span className="test-badge">
                <span /> TEST
              </span>
            )}
            <button
              className="round-button"
              aria-label="About this preview"
              onClick={() => setDialog("about")}
            >
              <Ellipsis size={22} />
            </button>
          </div>
        </header>

        <section className="viewing-stage" aria-label="Video website">
          {blocked ? (
            <div className="connection-state" role="alert">
              <div className="state-icon">
                <WifiOff size={26} />
              </div>
              <p className="eyebrow">A LITTLE PAUSE</p>
              <h1>Let’s reconnect.</h1>
              <p>{blocked}</p>
              <button
                className="primary-button"
                onClick={() => setAttempt((value) => value + 1)}
              >
                <RefreshCw size={17} /> Try again
              </button>
            </div>
          ) : !destination ? (
            <div className="connection-state" role="status">
              <LoaderCircle className="spin" size={30} />
              <p>Getting your story ready…</p>
            </div>
          ) : target?.mode === "navigate" ? (
            <div className="connection-state">
              <div className="state-icon">
                <ArrowUpRight size={28} />
              </div>
              <p className="eyebrow">YOUR NEXT STORY</p>
              <h1>Ready when you are.</h1>
              <p>Continue to {destination.name} to start watching.</p>
              <a className="primary-button" href={target.url} onClick={() => track("video_open")}>
                Start watching <ArrowUpRight size={18} />
              </a>
            </div>
          ) : target ? (
            <>
              {!frameLoaded && (
                <div className="frame-loading" role="status">
                  <LoaderCircle className="spin" size={28} />
                  <span>Opening your story…</span>
                </div>
              )}
              <iframe
                key={`${target.url}:${attempt}`}
                ref={iframeRef}
                className="video-site-frame"
                src={target.url}
                title={target.title}
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
                referrerPolicy="strict-origin-when-cross-origin"
                onLoad={() => {
                  track("frame_loaded");
                  setFrameLoaded(true);
                  setFrameSlow(false);
                }}
              />
              {frameSlow && (
                <div className="slow-notice">
                  <span>This is taking a little longer.</span>
                  <button onClick={() => setAttempt((value) => value + 1)}>
                    Retry
                  </button>
                  <a href={target.url} onClick={() => track("video_open")}>
                    Open directly <ArrowUpRight size={14} />
                  </a>
                </div>
              )}
            </>
          ) : null}
        </section>

        <footer className="portal-footer">
          <div>
            {target?.demo && <button className="viewer-save-button" disabled={saving} onClick={async () => { setSaving(true); try { await viewer.changeItem({ seriesId: "sintel-preview", title: "Sintel", saved: !viewer.items.find(x => x.seriesId === "sintel-preview")?.saved }); } catch { /* Shared sync notice shows the failure. */ } finally { setSaving(false); } }}>{viewer.items.find(x => x.seriesId === "sintel-preview")?.saved ? "✓ Saved" : "+ Save story"}</button>}
            <span className="footer-status" />
            <span>
              {target?.demo
                ? "Sample video · Playback preview"
                : "Your stories, one tap away."}
            </span>
          </div>
          {standalone ? (
            <span className="installed-label">
              <Check size={15} /> On your Home Screen
            </span>
          ) : (
            <button
              className="install-button"
              onClick={() => void install()}
              disabled={installing}
            >
              <ArrowDownToLine size={16} /> Add to Home Screen{" "}
              <ChevronRight size={15} />
            </button>
          )}
        </footer>
        {viewer.error && <p className="viewer-sync-error" role="status">{viewer.error} <a href="/account">My account</a></p>}
        {needRefresh && (
          <div className="update-notice" role="status">
            <span>A new version is ready.</span>
            <button onClick={() => void updateServiceWorker(true)}>
              Update now <RefreshCw size={13} />
            </button>
          </div>
        )}
      </main>

      {dialog && (
        <dialog
          ref={dialogRef}
          className="portal-dialog"
          onCancel={() => setDialog(null)}
          onClick={(event) => {
            if (event.target === event.currentTarget) setDialog(null);
          }}
        >
          <div className="dialog-top">
            <span className="dialog-grip" />
            <button
              className="round-button"
              onClick={() => setDialog(null)}
              aria-label="Close dialog"
            >
              <X size={20} />
            </button>
          </div>
          {dialog === "install" ? (
            <>
              <img
                className="install-icon"
                src="/icons/apple-touch-icon.png"
                alt="Yestv app icon"
              />
              <p className="eyebrow">KEEP YOUR NEXT STORY CLOSE</p>
              <h2>One tap. You’re back.</h2>
              <p>Add Yestv to your iPhone Home Screen for easy access.</p>
              <ol className="install-steps">
                <li>
                  <span className="step-icon">
                    <ArrowUpRight size={19} />
                  </span>
                  <span>
                    Open this page in <strong>Safari</strong>.
                  </span>
                </li>
                <li>
                  <span className="step-icon">
                    <Share size={19} />
                  </span>
                  <span>
                    Tap <strong>Share</strong> in the browser menu.
                  </span>
                </li>
                <li>
                  <span className="step-icon">
                    <Plus size={20} />
                  </span>
                  <span>
                    Choose <strong>Add to Home Screen</strong>, enable{" "}
                    <strong>Open as Web App</strong> if shown, then tap Add.
                  </span>
                </li>
              </ol>
              <p className="dialog-note">
                On a computer, use your browser’s install option when available.
              </p>
              <button
                className="primary-button full-width"
                onClick={() => setDialog(null)}
              >
                Got it
              </button>
            </>
          ) : (
            <>
              <div className="state-icon">
                <Info size={26} />
              </div>
              <p className="eyebrow">YESTV PREVIEW</p>
              <h2>Just press play.</h2>
              <p>
                {target?.demo
                  ? "This preview opens a sample video page so you can try the viewing experience. The existing video website has not been connected yet."
                  : "Yestv opens the configured video website. Your video account and purchases are managed by that website."}
              </p>
              {target?.demo && <div className="preview-facts">
                <span>
                  <ShieldCheck size={18} /> No sign-in or payment required
                </span>
                <span>Sample footage: Sintel, © Blender Foundation</span>
                <a
                  href="https://www.sintel.org/"
                  target="_blank"
                  rel="noreferrer"
                >
                  Sintel · CC BY 3.0 <ArrowUpRight size={14} />
                </a>
              </div>}
              <p className="dialog-note">
                Yestv {config.version} · {config.environment}
              </p>
              {target && (
                <a className="secondary-button full-width" href={target.url} onClick={() => track("video_open")}>
                  Open video page directly <ArrowUpRight size={16} />
                </a>
              )}
            </>
          )}
        </dialog>
      )}
    </div>
  );
}
